import React, { useState, useEffect, useRef } from "react";
import {
  FolderKanban,
  CalendarCheck,
  ClipboardCheck,
  MapPin,
  Download,
  Landmark,
  CheckCircle,
  TrendingUp,
  Award,
  BarChart3,
  AlertCircle,
  Star,
  Target,
  PieChart as PieChartIcon,
  FileText,
  Eye,
  Building2,
  Users,
  Globe
} from "lucide-react";
import Button from "components/ui/Button";
import MetricCard from "components/dashboard/MetricCard";
import DashboardChartFrame from "components/dashboard/DashboardChartFrame";
import DevelopmentDimensionPieCard from "components/dashboard/DevelopmentDimensionPieCard";
import RegionalAlertPanel from "components/dashboard/RegionalAlertPanel";
import { useLocationContext } from "context/LocationContext";
import { formatIgfCurrency, getRegionalDashboardData, YEARS } from "utils/dashboardData";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Line
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { classifyPerformance } from "utils/utils";

// Get performance color
function getPerformanceColor(classification) {
  switch (classification) {
    case "Excellent": return "text-green-600";
    case "Very Good": return "text-emerald-500";
    case "Good": return "text-blue-500";
    case "Satisfactory": return "text-yellow-600";
    default: return "text-red-500";
  }
}

// Get performance badge class
function getPerformanceBadgeClass(classification) {
  switch (classification) {
    case "Excellent": return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
    case "Very Good": return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300";
    case "Good": return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
    case "Satisfactory": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
    default: return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
  }
}

export default function RegionalDashboard() {
  const currentYear = new Date().getFullYear();
  const defaultYear = String(currentYear - 1);

  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const dashboardRef = useRef(null);
  const { orgTree, currentLocation } = useLocationContext();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await getRegionalDashboardData(orgTree, currentLocation, selectedYear);
        setDashboardData(data);
      } catch (error) {
        console.error("Error loading regional dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [orgTree, currentLocation, selectedYear]);

  // PDF Export Function
  const exportToPDF = async () => {
    if (!dashboardRef.current) return;

    setIsExporting(true);

    try {
      const element = dashboardRef.current;
      const originalOverflow = element.style.overflow;
      element.style.overflow = 'visible';

      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: false
      });

      element.style.overflow = originalOverflow;

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdf.internal.pageSize.height;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdf.internal.pageSize.height;
      }

      pdf.save(`regional-dashboard-${dashboardData?.regionName}-${selectedYear}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Preview PDF Function
  const previewPDF = async () => {
    if (!dashboardRef.current) return;

    setIsExporting(true);
    setShowPreview(true);

    try {
      const element = dashboardRef.current;
      const originalOverflow = element.style.overflow;
      element.style.overflow = 'visible';

      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: false
      });

      element.style.overflow = originalOverflow;

      const imgData = canvas.toDataURL('image/png');
      setPreviewUrl(imgData);

    } catch (error) {
      console.error('Error generating preview:', error);
      alert('Failed to generate preview. Please try again.');
      setShowPreview(false);
    } finally {
      setIsExporting(false);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewUrl(null);
  };

  const downloadFromPreview = () => {
    if (previewUrl) {
      const link = document.createElement('a');
      link.download = `regional-dashboard-${dashboardData?.regionName}-${selectedYear}.png`;
      link.href = previewUrl;
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="main-content flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
          <p className="text-sm text-muted-foreground mt-2">Loading regional dashboard for {selectedYear}...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="main-content flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="bg-muted/30 rounded-full p-3 mb-3">
            <AlertCircle className="h-8 w-8 text-muted-foreground/50" />
          </div>
          <p className="text-sm text-muted-foreground">No data available for this region</p>
          <p className="text-xs text-muted-foreground/70 mt-1">Please select a different region or year</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-sm mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
              {entry.name === "Execution Rate" && "%"}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const ProjectsPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const total = dashboardData.projectsByDimension?.reduce((sum, d) => sum + d.value, 0) || 0;
      const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg max-w-xs">
          <p className="font-semibold text-sm mb-1">{data.name}</p>
          <div className="space-y-1 text-xs">
            <p className="text-muted-foreground">
              Count: <span className="font-medium text-foreground">{data.value}</span>
            </p>
            <p className="text-muted-foreground">
              Percentage: <span className="font-medium text-foreground">{percentage}%</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const AAPPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const total = dashboardData.aapByDimension?.reduce((sum, d) => sum + d.value, 0) || 0;
      const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg max-w-xs">
          <p className="font-semibold text-sm mb-1">{data.name}</p>
          <div className="space-y-1 text-xs">
            <p className="text-muted-foreground">
              Count: <span className="font-medium text-foreground">{data.value}</span>
            </p>
            <p className="text-muted-foreground">
              Percentage: <span className="font-medium text-foreground">{percentage}%</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Prepare data for district performance chart
  const districtData = dashboardData.metrics.projects.districtData || [];
  const activityPlanData = dashboardData.metrics.activityPlan.districtData || [];
  const meetingsData = dashboardData.metrics.meetings.districtData || [];
  const igfData = dashboardData.metrics.igfCollected.districtData || [];

  return (
    <>
      <div className="main-content space-y-6 animate-fade-in">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 text-xl font-bold sm:text-2xl">
              <MapPin className="h-6 w-6 text-accent" />
              Regional Dashboard
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {dashboardData.subtitle}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-medium text-foreground">
                Yearly View - {dashboardData.year}
              </span>
              <span className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-medium text-foreground">
                {dashboardData.totalDistricts || dashboardData.districtRows?.length || 0} Districts
              </span>
            </div>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center lg:justify-end">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="
                w-full
                sm:w-auto
                appearance-none
                rounded-xl
                border border-slate-300
                bg-white
                px-4
                py-2.5
                pr-10
                text-sm
                font-medium
                text-slate-700
                shadow-sm
                transition-all
                duration-200
                hover:border-slate-400
                focus:border-blue-500
                focus:outline-none
                focus:ring-4
                focus:ring-blue-100
                dark:border-slate-700
                dark:bg-slate-900
                dark:text-slate-200
                dark:hover:border-slate-600
                dark:focus:border-blue-400
                dark:focus:ring-blue-900/30
              "
            >
              {YEARS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <Button
              className="w-full sm:w-auto"
              variant="outline"
              size="sm"
              type="button"
              onClick={previewPDF}
              disabled={isExporting}
            >
              <Eye className="h-4 w-4 mr-1" /> Preview
            </Button>
            <Button
              className="w-full sm:w-auto"
              variant="outline"
              size="sm"
              type="button"
              onClick={exportToPDF}
              disabled={isExporting}
            >
              {isExporting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent mr-1"></div>
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              Export PDF
            </Button>
          </div>
        </div>

        {/* {JSON.stringify(dashboardData?.districtRows)} */}

        {/* Dashboard Content */}
        <div ref={dashboardRef}>
          {/* Top 6 Metric Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <MetricCard
              title="Activity Plans"
              value={dashboardData.metrics.activityPlan.value}
              icon={ClipboardCheck}
              status="info"
            />
            <MetricCard
              title="Projects"
              value={dashboardData.metrics.projects.value}
              icon={FolderKanban}
              status="success"
            />
            <MetricCard
              title="Programmes"
              value={dashboardData.metrics.programmes.value}
              icon={CalendarCheck}
              status="warning"
            />
            <MetricCard
              title="Activity Executed"
              value={dashboardData.metrics.activityExecuted.value}
              subMetrics={dashboardData.metrics.activityExecuted.subMetrics}
              icon={CheckCircle}
              status="success"
              footer={
                <div className="mt-2 pt-2 border-t border-border">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-muted-foreground">Execution Rate:</span>
                    <span className="font-semibold text-green-600">
                      {dashboardData.metrics.activityExecuted.executionRate}%
                    </span>
                  </div>
                </div>
              }
            />
            <MetricCard
              title="Meetings"
              value={dashboardData.metrics.meetings.value}
              icon={CalendarCheck}
              status="success"
            />
            <MetricCard
              title="IGF Collected"
              value={dashboardData.metrics.igfCollected.value}
              icon={Landmark}
              status="info"
            />
          </div>

          {/* Combined Chart: Activity Plan vs Executed by District */}
          {dashboardData.planVsExecutedData && dashboardData.planVsExecutedData.length > 0 && (
            <div className="metric-card mt-6">
              <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between">
                <h3 className="font-semibold text-sm">Activity Plan vs Executed by District</h3>
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "hsl(215, 60%, 22%)" }}></div>
                    <span>Planned</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "hsl(142, 70%, 45%)" }}></div>
                    <span>Executed (Projects + Programmes)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "hsl(38, 92%, 50%)" }}></div>
                    <span>Execution Rate %</span>
                  </div>
                </div>
              </div>
              <DashboardChartFrame minWidthClass="min-w-[760px] md:min-w-0">
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={dashboardData.planVsExecutedData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 88%)" />
                    <XAxis
                      dataKey="district"
                      tick={{ fontSize: 11 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 100]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="plan"
                      name="Planned Activities"
                      fill="hsl(215, 60%, 22%)"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="executed"
                      name="Executed Activities"
                      fill="hsl(142, 70%, 45%)"
                      radius={[4, 4, 0, 0]}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="executionRate"
                      name="Execution Rate %"
                      stroke="hsl(38, 92%, 50%)"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </DashboardChartFrame>
            </div>
          )}

          {/* Pie Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Projects by Development Dimension */}
            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">Projects by Development Dimension</h3>
              {dashboardData.projectsByDimension && dashboardData.projectsByDimension.length > 0 ? (
                <DashboardChartFrame minWidthClass="min-w-[540px] sm:min-w-0">
                  <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={dashboardData.projectsByDimension}
                      dataKey="value"
                      nameKey="shortName"
                      cx="50%"
                      cy="50%"
                      outerRadius={130}
                      innerRadius={70}
                      paddingAngle={2}
                    >
                      {dashboardData.projectsByDimension.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<ProjectsPieTooltip />} />
                    <Legend
                      wrapperStyle={{ fontSize: 11, maxHeight: 140, overflowY: 'auto' }}
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      formatter={(value, entry, index) => {
                        const data = dashboardData.projectsByDimension[index];
                        return <span className="text-xs">{data.shortName}</span>;
                      }}
                    />
                  </PieChart>
                  </ResponsiveContainer>
                </DashboardChartFrame>
              ) : (
                <div className="flex items-center justify-center h-[400px]">
                  <p className="text-sm text-muted-foreground">No project dimension data available</p>
                </div>
              )}
              <p className="text-xs text-muted-foreground text-center mt-2">
                Hover over chart sections to see full dimension names
              </p>
            </div>

            {/* Activity Plans by Development Dimension */}
            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">Activity Plans by Development Dimension</h3>
              {dashboardData.aapByDimension && dashboardData.aapByDimension.length > 0 ? (
                <DashboardChartFrame minWidthClass="min-w-[540px] sm:min-w-0">
                  <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={dashboardData.aapByDimension}
                      dataKey="value"
                      nameKey="shortName"
                      cx="50%"
                      cy="50%"
                      outerRadius={130}
                      innerRadius={70}
                      paddingAngle={2}
                    >
                      {dashboardData.aapByDimension.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<AAPPieTooltip />} />
                    <Legend
                      wrapperStyle={{ fontSize: 11, maxHeight: 140, overflowY: 'auto' }}
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      formatter={(value, entry, index) => {
                        const data = dashboardData.aapByDimension[index];
                        return <span className="text-xs">{data.shortName}</span>;
                      }}
                    />
                  </PieChart>
                  </ResponsiveContainer>
                </DashboardChartFrame>
              ) : (
                <div className="flex items-center justify-center h-[400px]">
                  <p className="text-sm text-muted-foreground">No activity plan dimension data available</p>
                </div>
              )}
              <p className="text-xs text-muted-foreground text-center mt-2">
                Hover over chart sections to see full dimension names
              </p>
            </div>
          </div>

          {/* Other District Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Projects by District */}
            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">Projects by District</h3>
              <DashboardChartFrame>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={districtData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 88%)" />
                    <XAxis dataKey="district" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="total" fill="hsl(174, 60%, 40%)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </DashboardChartFrame>
            </div>

            {/* Programmes by District */}
            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">Programmes by District</h3>
              <DashboardChartFrame>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dashboardData.metrics.programmes.districtData || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 88%)" />
                    <XAxis dataKey="district" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="total" fill="hsl(38, 92%, 50%)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </DashboardChartFrame>
            </div>

            {/* Meetings by District */}
            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">Meetings by District</h3>
              <DashboardChartFrame>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={meetingsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 88%)" />
                    <XAxis dataKey="district" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="total" fill="hsl(262, 52%, 55%)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </DashboardChartFrame>
            </div>

            {/* IGF Collected by District */}
            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">IGF Collected by District (GHS)</h3>
              <DashboardChartFrame>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={igfData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 88%)" />
                    <XAxis dataKey="district" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => formatIgfCurrency(value)} />
                    <Bar dataKey="totalAmount" fill="hsl(142, 70%, 45%)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </DashboardChartFrame>
            </div>
          </div>

          {/* District Performance Table */}
          {dashboardData.districtRows && dashboardData.districtRows.length > 0 && (
            <div className="metric-card mt-6">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="h-5 w-5 text-accent" />
                <h3 className="font-semibold text-sm">District Performance Overview</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[980px] w-full text-sm">
                  <thead className="border-b border-border">
                    <tr className="text-left">
                      <th className="pb-2 font-medium text-muted-foreground w-16">Position</th>
                      <th className="pb-2 font-medium text-muted-foreground">District</th>
                      <th className="pb-2 font-medium text-muted-foreground">Activities Planed</th>
                      <th className="pb-2 font-medium text-muted-foreground">Activities Executed</th>
                      <th className="pb-2 font-medium text-muted-foreground">Projects</th>
                      <th className="pb-2 font-medium text-muted-foreground">Programmes</th>
                      <th className="pb-2 font-medium text-muted-foreground">Meetings</th>
                      <th className="pb-2 font-medium text-muted-foreground">IGF Collected(GHS)</th>
                      <th className="pb-2 font-medium text-muted-foreground">Execution Rate</th>
                      <th className="pb-2 font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.districtRows.map((district, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                        <td className="py-3 font-semibold text-muted-foreground">{idx + 1}</td>
                        <td className="py-3 font-medium">{district.name}</td>
                        <td className="py-3">{district.activityPlans || 0}</td>
                        <td className="py-3">{district.projects + district.programmes}</td>
                        <td className="py-3">{district.projects || 0}</td>
                        <td className="py-3">{district.programmes || 0}</td>
                        <td className="py-3">{district.meetings || 0}</td>
                        <td className="py-3">{formatIgfCurrency(district.igfCollected || 0)}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-muted rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full transition-all"
                                style={{ width: `${district.planExecutionPercentage}%` }}
                              />
                            </div>
                            <span className="text-xs">{district.planExecutionPercentage}%</span>
                          </div>
                        </td>

                        <td className="py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${district.status === "Good"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                              : district.status === "On Track"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                                : district.status === "At Risk"
                                  ? "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300"
                                  : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                            }`}>
                            {district.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <RegionalAlertPanel
            alerts={dashboardData.alerts || []}
            regionName={dashboardData.regionName}
          />
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPreview && previewUrl && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm animate-fade-in sm:items-center sm:p-4">
          <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl dark:bg-slate-900 sm:max-h-[90vh] sm:rounded-2xl">
            <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-2">
                <FileText className="h-5 w-5 text-accent" />
                <h3 className="truncate text-sm font-semibold sm:text-base">Dashboard Preview - {dashboardData?.regionName} ({selectedYear})</h3>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Button
                  className="w-full sm:w-auto"
                  variant="outline"
                  size="sm"
                  onClick={downloadFromPreview}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download as PNG
                </Button>
                <Button
                  className="w-full sm:w-auto"
                  variant="outline"
                  size="sm"
                  onClick={closePreview}
                >
                  Close
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <img
                src={previewUrl}
                alt="Dashboard Preview"
                className="w-full h-auto rounded-lg shadow-lg"
                style={{ maxHeight: 'calc(90vh - 80px)', objectFit: 'contain' }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
